import { openCreateFinancialDeckModal } from "@/components/CreateFinancialDeckModal/openCreateFinancialDeckModal";
import { DealRecord, getCurrentDealEntity } from "@/models/deal";
import {
  FinancialDeckRecord,
  cloneFinancialDeck,
  financialDeckModel,
} from "@/models/financialDecks";
import { userIsCreatorConstraint } from "@/orm/model";
import { Entity, belongsToIds } from "@/orm/types";
import { Route } from "@/paths";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  ActionIcon,
  Group,
  Menu,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { useAtomValue } from "jotai";
import { BiPlusCircle } from "react-icons/bi";
import { BsFileEarmarkSpreadsheetFill, BsThreeDots } from "react-icons/bs";
import { LiaClone, LiaTrashAltSolid } from "react-icons/lia";
import { RiPushpin2Fill, RiPushpinLine } from "react-icons/ri";
import { generatePath } from "react-router-dom";

export function ModelsList() {
  const [parent] = useAutoAnimate(/* optional config */);
  const dealEntity = getCurrentDealEntity();

  const { data: deal } = useAtomValue(dealEntity.getObserveEntityRecordAtom());

  const { entities: deckEntites } = useAtomValue(
    dealEntity.getObserveManyBelongingsAtom<FinancialDeckRecord>(
      financialDeckModel,
      "belongsToDealIds",
      {
        constraints: [userIsCreatorConstraint()],
      },
    ),
  );

  const orderedDeckEntities = (deckEntites || []).sort((a, b) => {
    // Check if either model is pinned
    const aPinned = deal?.pinnedFinancialDeckId === a.id ? -1 : 0;
    const bPinned = deal?.pinnedFinancialDeckId === b.id ? -1 : 0;

    // If both are pinned or neither is pinned, sort by createdAt
    if (aPinned === bPinned) {
      return b!.data!.createdAt?.toMillis() - a!.data!.createdAt?.toMillis();
    }

    // Otherwise, sort by pinned status
    return aPinned - bPinned;
  });

  const confirmDeleteDeck = (deckEntity: Entity<FinancialDeckRecord>) => {
    if (!deckEntity) return;

    modals.openConfirmModal({
      title: "Please confirm your action",
      children: (
        <Text size="sm">
          Are you sure you want to delete the model{" "}
          <strong>"{deckEntity.data!.name}"</strong>? This cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete Model", cancel: "Cancel" },
      confirmProps: { color: "red" },
      //onCancel: () => console.log("Cancel"),
      onConfirm: () => deckEntity.remove(),
    });
  };

  const onClone = (deckEntity: Entity<FinancialDeckRecord>) => {
    cloneFinancialDeck(deckEntity, {
      belongsToDealIds: belongsToIds<DealRecord>([dealEntity.id]),
    });
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Financial Models
        </Text>
        <Tooltip label="Create New Model" withArrow>
          <ActionIcon
            variant="transparent"
            size={"sm"}
            onClick={() => openCreateFinancialDeckModal(dealEntity)}
          >
            <BiPlusCircle size={"100%"} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <ScrollArea
        mah={300}
        offsetScrollbars={(orderedDeckEntities?.length || 0) > 4}
      >
        <Stack gap={"xs"} ref={parent}>
          {orderedDeckEntities?.map((deck) => (
            <UnstyledButton
              bg={"white"}
              style={{
                border: "1px solid var(--mantine-color-gray-4)",
              }}
              pr="sm"
              key={deck.id}
            >
              <Group gap={0}>
                <Group
                  p={"sm"}
                  onClick={() => {
                    window.open(
                      generatePath(Route.ModelTab, {
                        modelId: deck.id,
                        modelTabId: "historicals",
                      }),
                      "_blank",
                      "noreferrer",
                    );
                  }}
                  flex={1}
                >
                  <ThemeIcon variant="white" color="blue">
                    <BsFileEarmarkSpreadsheetFill size={32} />
                  </ThemeIcon>
                  <Stack flex={1} gap={4}>
                    <Text size="sm" fw={500} lh={1.2}>
                      {deck.data.name}
                    </Text>

                    <Text c="dimmed" size="xs">
                      Created on{" "}
                      {deck.data.createdAt
                        ? deck.data.createdAt.toDate().toLocaleDateString()
                        : ""}
                    </Text>
                  </Stack>
                </Group>
                {(orderedDeckEntities.length || 0) > 1 && (
                  <Tooltip label="Pin to top" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      component="p"
                      onClick={() => {
                        if (deck.id === deal?.pinnedFinancialDeckId) return;

                        dealEntity.update({
                          pinnedFinancialDeckId:
                            deal!.pinnedFinancialDeckId === deck.id
                              ? null
                              : deck.id,
                        });
                      }}
                    >
                      {deck.id === deal?.pinnedFinancialDeckId ? (
                        <RiPushpin2Fill size={22} />
                      ) : (
                        <RiPushpinLine size={22} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                )}

                <Menu
                  transitionProps={{ transition: "pop" }}
                  withArrow
                  arrowPosition="center"
                  position="bottom-end"
                >
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" component="p">
                      <BsThreeDots size={22} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<LiaClone size={18} />}
                      onClick={() => onClone(deck)}
                    >
                      Clone
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<LiaTrashAltSolid size={18} />}
                      color="red"
                      onClick={() => confirmDeleteDeck(deck)}
                    >
                      Delete Model
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
